import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import * as MuiIcons from '@material-ui/icons';
import PropTypes from 'prop-types';

const MenuList = React.forwardRef((props, ref) => {
  const scrollContainer = useRef(null);
  const activeItem = useRef(null);
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);
  const [selectedCommandIndex, setSelectedCommandIndex] = useState(0);
  const { items, command } = props;
  useEffect(() => {
    setSelectedGroupIndex(0);
    setSelectedCommandIndex(0);
  }, [items]);

  const selectItem = useCallback(
    (groupIndex, commandIndex) => {
      const selectedCommand = items[groupIndex].commands[commandIndex];
      command(selectedCommand);
    },
    [items, command],
  );

  React.useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowDown') {
        if (!items.length) return false;

        const { commands } = items[selectedGroupIndex];
        let newCommandIndex = selectedCommandIndex + 1;
        let newGroupIndex = selectedGroupIndex;

        if (commands.length - 1 < newCommandIndex) {
          newCommandIndex = 0;
          newGroupIndex = selectedGroupIndex + 1;
        }

        if (items.length - 1 < newGroupIndex) {
          newGroupIndex = 0;
        }

        setSelectedCommandIndex(newCommandIndex);
        setSelectedGroupIndex(newGroupIndex);

        return true;
      }

      if (event.key === 'ArrowUp') {
        if (!items.length) return false;

        let newCommandIndex = selectedCommandIndex - 1;
        let newGroupIndex = selectedGroupIndex;

        if (newCommandIndex < 0) {
          newGroupIndex = selectedGroupIndex - 1;
          newCommandIndex = items[newGroupIndex]?.commands.length - 1 || 0;
        }

        if (newGroupIndex < 0) {
          newGroupIndex = items.length - 1;
          newCommandIndex = items[newGroupIndex].commands.length - 1;
        }

        setSelectedCommandIndex(newCommandIndex);
        setSelectedGroupIndex(newGroupIndex);

        return true;
      }

      if (event.key === 'Enter') {
        if (!items.length || selectedGroupIndex === -1 || selectedCommandIndex === -1) return false;

        selectItem(selectedGroupIndex, selectedCommandIndex);

        return true;
      }

      return false;
    },
  }));

  useEffect(() => {
    if (activeItem.current && scrollContainer.current) {
      const { offsetTop } = activeItem.current;
      const { offsetHeight } = activeItem.current;
      scrollContainer.current.scrollTop = offsetTop - offsetHeight;
    }
  }, [selectedCommandIndex, selectedGroupIndex]);

  const createCommandClickHandler = useCallback(
    (groupIndex, commandIndex) => () => selectItem(groupIndex, commandIndex),
    [selectItem],
  );

  if (!items.length) return null;

  return (
    <div ref={scrollContainer} className="text-black max-h-[min(80vh,24rem)] overflow-auto flex-wrap mb-8 p-2 bg-white rounded shadow modal-safe-click-zone">
      <div className="grid grid-cols-1 gap-0.5">
        {items.map((group, groupIndex) => (
          <React.Fragment key={`${group.title}-wrapper`}>
            <div className="text-neutral-500 text-[0.65rem] col-[1/-1] mx-2 mt-4 font-semibold tracking-wider select-none uppercase first:mt-0.5">
              {group.title}
            </div>
            {group.commands.map((cmd, commandIndex) => {
              const IconComponent = cmd.icon
              || MuiIcons.HelpOutline;
              const isActive = selectedGroupIndex === groupIndex
              && selectedCommandIndex === commandIndex;
              return (
                <button
                  type="button"
                  key={`${cmd.label}`}
                  ref={isActive ? activeItem : null}
                  onClick={createCommandClickHandler(groupIndex, commandIndex)}
                  className={`flex items-center gap-2 px-2 py-1 rounded text-sm ${isActive ? 'bg-neutral-100 text-black' : 'hover:bg-neutral-50'}`}
                >
                  <div className="text-gray-500">
                    <IconComponent className="w-4 h-4" />
                  </div>

                  {cmd.label}
                </button>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
});

MenuList.displayName = 'MenuList';
MenuList.propTypes = {
  items: PropTypes.arrayOf.isRequired,
  command: PropTypes.func.isRequired,
};
export default MenuList;
